import React from "react";
import { motion } from "framer-motion";
import { ChevronRight, MessageCircle } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import { useTheme } from "../../../../context/ThemeContext";
import { ReactComponent as DrillingIcon } from '../../../../assets/drilling.svg';
import { ReactComponent as ReservoirIcon } from '../../../../assets/reservoir.svg';
import { ReactComponent as ManufacturingIcon } from '../../../../assets/manufacturing.svg';

const Benefits = () => {
    const navigate = useNavigate();
    const { isDarkTheme } = useTheme();

    const benefits = [
        {
            title: "Drilling Optimization",
            icon: <DrillingIcon className={`w-10 h-10 ${isDarkTheme ? 'fill-amber-400' : 'fill-amber-600'}`} />,
            features: [
                "Real-time torque calculations",
                "Well path optimization",
                "Equipment failure prediction",
                "Cost analysis modeling"
            ],
            model: "drilling"
        },
        {
            title: "Reservoir Analysis",
            icon: <ReservoirIcon className={`w-10 h-10 ${isDarkTheme ? 'fill-amber-400' : 'fill-amber-600'}`} />,
            features: [
                "Production forecasting",
                "Fluid dynamics simulation",
                "Reserve estimation",
                "Enhanced recovery strategies"
            ],
            model: "reservoir"
        },
        {
            title: "Petroleum Production Insights",
            icon: <ManufacturingIcon className={`w-10 h-10 ${isDarkTheme ? 'fill-amber-400' : 'fill-amber-600'}`} />,
            features: [
                "Material stress analysis",
                "Supply chain optimization",
                "Quality control algorithms",
                "Equipment lifecycle planning"
            ],
            model: "manufacturing"
        }
    ];

    return (
        <div className={`${isDarkTheme ? 'bg-black text-white' : 'bg-white text-black'} py-24 px-4 relative overflow-hidden`} id="benefits">
            <motion.div
                className="max-w-7xl mx-auto"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8 }}
            >
                {/* Header Section */}
                <div className="text-center mb-16">
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2 }}
                        className={`text-3xl font-bold mb-4 ${
                            isDarkTheme 
                                ? 'text-amber-400' 
                                : 'text-amber-600'
                        }`}
                    >
                        Why Choose OG-FLEET?
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.4 }}
                        className={`${
                            isDarkTheme 
                                ? 'text-gray-300' 
                                : 'text-gray-600'
                        } text-base max-w-2xl mx-auto`}
                    >
                        Specialized AI models trained on decades of petroleum engineering knowledge
                    </motion.p>
                </div>

                {/* Benefits Grid */}
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {benefits.map((benefit, index) => (
                        <motion.div
                            key={index}
                            className={`group ${
                                isDarkTheme
                                    ? 'bg-gray-900 border-gray-700 hover:bg-gray-800 text-white'
                                    : 'bg-white border-gray-200 hover:bg-amber-50 text-black'
                            } border rounded-xl p-6 relative hover:shadow-lg transition-all`}
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "0px 0px -100px 0px" }}
                            transition={{ delay: index * 0.2, duration: 0.5 }}
                            whileHover={{ scale: 1.02 }}
                        >
                            {/* Icon Container */}
                            <div className={`mb-4 p-3 rounded-full w-max mx-auto ${
                                isDarkTheme 
                                    ? 'bg-gray-800' 
                                    : 'bg-indigo-50'
                            }`}>
                                {benefit.icon}
                            </div>

                            <h3 className={`text-xl font-semibold text-center mb-3 ${
                                isDarkTheme 
                                    ? 'text-white' 
                                    : 'text-gray-800'
                            }`}>
                                {benefit.title}
                            </h3>

                            <ul className="space-y-2 mb-4">
                                {benefit.features.map((feature, fIndex) => (
                                    <motion.li
                                        key={fIndex}
                                        className={`flex items-center text-sm gap-2 ${
                                            isDarkTheme 
                                                ? 'text-gray-300' 
                                                : 'text-gray-600'
                                        }`}
                                        initial={{ x: -20 }}
                                        animate={{ x: 0 }}
                                        transition={{ delay: fIndex * 0.1 }}
                                    >
                                        <ChevronRight className={`w-4 h-4 ${
                                            isDarkTheme 
                                                ? 'text-amber-400' 
                                                : 'text-amber-600'
                                        }`} />
                                        <span>{feature}</span>
                                    </motion.li>
                                ))}
                            </ul>

                            {/* Hover Border */}
                            <div className={`absolute inset-0 border-2 border-transparent group-hover:${
                                isDarkTheme 
                                    ? 'border-amber-400/20' 
                                    : 'border-amber-600/20'
                            } rounded-xl transition-all pointer-events-none`} />
                        </motion.div>
                    ))}
                </div>

                {/* CTA Section */}
                <motion.div
                    className="text-center mt-16"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.6 }}
                >
                    <div 
                        onClick={() => navigate('/auth')}
                        className={`inline-flex items-center gap-3 rounded-full px-6 py-3 transition-colors cursor-pointer ${
                            isDarkTheme
                                ? 'bg-amber-400 text-black hover:bg-amber-300'
                                : 'bg-amber-600 text-white hover:bg-amber-700'
                        }`}
                    >
                        <MessageCircle className="w-6 h-6" />
                        <span className="text-base">
                            Start Chatting with AI Experts
                        </span>
                    </div>
                </motion.div>
            </motion.div>
        </div>
    );
};

export default Benefits;